<template>
  <nav id="nav" role="navigation" class="navbar is-dark">
    <div class="navbar-brand">
      <!-- Home Icon -->
      <router-link id="home-link" to="/" class="navbar-item">
        <img id="logo" src="@/assets/images/Heavybit-Symbol-RGB-Light.svg" />
      </router-link>

      <!-- Burger-->
      <a
        role="button"
        class="navbar-burger"
        :class="{ 'is-active': isOpen('burger') }"
        aria-label="menu"
        :aria-expanded="isOpen('burger') ? 'true' : 'false'"
        @click.prevent="toggleMenu('burger')"
      >
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </a>
    </div>

    <!-- Menu Items -->
    <div class="navbar-menu" :class="{ 'is-active': isOpen('burger') }">
      <!-- Default Menu Items-->
      <div class="navbar-start"></div>
      <div class="navbar-end">
        <router-link :to="{ name: 'home' }" class="navbar-item" @click="closeMenus">
          Home
        </router-link>
        <router-link :to="{ name: 'pipelines' }" class="navbar-item" @click="closeMenus">
          Deals
        </router-link>
        <router-link :to="{ name: 'members' }" class="navbar-item" @click="closeMenus">
          Portfolio
        </router-link>
        <router-link :to="{ name: 'funds' }" class="navbar-item" @click="closeMenus">
          Funds
        </router-link>
        <div class="navbar-item has-dropdown is-hoverable">
          <a
            class="navbar-link"
            :class="{ 'router-link-active': PROGRAM_PATHS.some((p) => route.path.startsWith(p)) }"
          >
            Program
          </a>
          <div class="navbar-dropdown">
            <router-link
              :to="{ name: 'events-overview' }"
              class="dropdown-item"
              :class="{ 'router-link-active': route.path.startsWith('/events') }"
              @click="closeMenus"
            >
              Events
            </router-link>
            <router-link
              :to="{ name: 'newsletters-lists' }"
              class="dropdown-item"
              :class="{ 'router-link-active': route.path.startsWith('/newsletters') }"
              @click="closeMenus"
            >
              Newsletters
            </router-link>
          </div>
        </div>
        <router-link :to="{ name: 'files' }" class="navbar-item" @click="closeMenus">
          Files
        </router-link>

        <!-- Search icon: prominent entry point. Rich search modal TBD (see #738). -->
        <router-link
          :to="{ name: 'search' }"
          class="navbar-item navbar-search"
          aria-label="Search"
          @click="closeMenus"
        >
          <font-awesome-icon icon="search" />
        </router-link>

        <!-- Source Menu -->
        <div class="navbar-item has-dropdown is-hoverable">
          <a class="navbar-link">
            <avatar-element
              :record="userStore.user?.record ?? undefined"
              :email="userEmail"
              size="x-small"
              fixed
            />
          </a>
          <div class="navbar-dropdown is-right">
            <router-link :to="{ name: 'settings' }" class="dropdown-item" @click="closeMenus">
              <font-awesome-icon icon="cog" fixed-width />
              Settings
            </router-link>
            <router-link
              v-if="isAdmin"
              :to="{ name: 'admin' }"
              class="dropdown-item"
              @click="closeMenus"
            >
              <font-awesome-icon icon="tools" fixed-width />
              Admin
            </router-link>
            <hr class="navbar-divider" />
            <a class="dropdown-item" @click.prevent="logout" @click="closeMenus">
              <font-awesome-icon icon="sign-out-alt" fixed-width />
              Sign out
            </a>
          </div>
        </div>
      </div>
    </div>
  </nav>
  <a id="feedback-button" class="text-7 rl-2">
    <font-awesome-icon icon="comment" fixed-width />
    <span class="text">&nbsp;Feedback</span>
  </a>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import AvatarElement from "@/components/elements/AvatarElement.vue"
import { type Shortcut, useMousetrap } from "@/composables/useMousetrap"
import type { AuthService } from "@/identity/auth"
import { useUserStore } from "@/stores/user"

const instance = getCurrentInstance()
const { addShortcuts, removeShortcuts } = useMousetrap()
const auth: AuthService | undefined = instance?.proxy?.$auth

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const PROGRAM_PATHS = ["/events", "/newsletters"]

const openMenu = ref<string | null>(null)

const isAdmin = computed(() => {
  return !!userStore.isAdmin
})

const userEmail = computed(() => {
  return userStore.email || ""
})

function logout() {
  userStore.startLogout()
  auth?.logout()
}

function isOpen(menu: string) {
  return openMenu.value === menu
}

function toggleMenu(menu: string) {
  openMenu.value = openMenu.value === menu ? null : menu
}

function closeMenus() {
  openMenu.value = null
}

function navigateTo(routeName: string) {
  if (routeName != route.name) router.push({ name: routeName })
}

const shortcuts: Shortcut[] = [
  { keys: "g h", group: "Navigation", label: "Go to home", fn: () => navigateTo("home") },
  { keys: "g m", group: "Navigation", label: "Go to members", fn: () => navigateTo("members") },
  { keys: "g c", group: "Navigation", label: "Go to calendar", fn: () => navigateTo("calendar") },
  { keys: "g f", group: "Navigation", label: "Go to financing", fn: () => navigateTo("financing") },
  { keys: "g s", group: "Navigation", label: "Go to search", fn: () => navigateTo("search") },
  { keys: "g u", group: "Navigation", label: "Go to funds", fn: () => navigateTo("funds") },
  { keys: "g d", group: "Navigation", label: "Go to deals", fn: () => navigateTo("pipelines") },
  { keys: "g i", group: "Navigation", label: "Go to inbox", fn: () => navigateTo("inbox") },
  { keys: "g t", group: "Navigation", label: "Go to tags", fn: () => navigateTo("tags") },
  { keys: "g l", group: "Navigation", label: "Go to files", fn: () => navigateTo("files") },
  {
    keys: "b",
    group: "Navigation",
    label: "Go back",
    fn: () => {
      if (route.name !== "home") router.go(-1)
    },
  },
]

onMounted(() => addShortcuts(shortcuts))

onBeforeUnmount(() => removeShortcuts(shortcuts))
</script>

<style>
/* Navbar structural styles */
.navbar {
  display: flex;
  align-items: stretch;
  min-height: 3.25rem;
  position: relative;

  @media screen and (max-width: 782px) {
    flex-wrap: wrap;
  }
}

.navbar-brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media screen and (max-width: 782px) {
    width: 100%;
  }
}

.navbar-burger {
  display: none;
  cursor: pointer;
  width: 3.25rem;
  height: 3.25rem;
  position: relative;
  margin-left: auto;
  appearance: none;
  background: none;
  border: none;
  color: inherit;

  span {
    display: block;
    height: 1px;
    left: calc(50% - 8px);
    position: absolute;
    width: 16px;
    background: currentColor;

    &:nth-child(1) {
      top: calc(50% - 6px);
    }
    &:nth-child(2) {
      top: calc(50% - 1px);
    }
    &:nth-child(3) {
      top: calc(50% + 4px);
    }
  }

  &.is-active span {
    &:nth-child(1) {
      transform: translateY(5px) rotate(45deg);
    }
    &:nth-child(2) {
      opacity: 0;
    }
    &:nth-child(3) {
      transform: translateY(-5px) rotate(-45deg);
    }
  }

  @media screen and (max-width: 782px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.navbar-menu {
  display: flex;
  flex-grow: 1;
  align-items: stretch;

  @media screen and (max-width: 782px) {
    display: none;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    padding: 0.5rem 0;

    &.is-active {
      display: flex;
    }
  }
}

.navbar-start,
.navbar-end {
  display: flex;
  align-items: stretch;

  @media screen and (max-width: 782px) {
    flex-direction: column;
    align-items: stretch;
  }
}
.navbar-start {
  margin-right: auto;
}
.navbar-end {
  margin-left: auto;
}

.navbar-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  position: relative;

  &.has-dropdown {
    cursor: pointer;
    padding: 0;

    &.is-hoverable:hover .navbar-dropdown,
    &.is-active .navbar-dropdown {
      display: block;
    }

    @media screen and (max-width: 782px) {
      .navbar-dropdown {
        position: static;
        display: block;
        box-shadow: none;
        padding-left: 0;
        background: transparent;
        border-radius: 0;

        .dropdown-item {
          color: inherit;
        }
      }
    }
  }
}

.navbar-link {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;

  &::after {
    content: "";
    display: block;
    width: 0.625em;
    height: 0.625em;
    border: 1.5px solid currentColor;
    border-right: 0;
    border-top: 0;
    border-radius: 2px;
    transform: rotate(-45deg);
    margin-left: 0.5em;
    margin-top: -0.25em;

    @media screen and (max-width: 782px) {
      display: none;
    }
  }
}

.navbar-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  display: none;
  min-width: 12rem;
  padding: 0.5rem 0;
  background: var(--auger-bg);
  border-radius: var(--auger-radius);
  box-shadow: var(--auger-shadow-3);

  &.is-right {
    left: auto;
    right: 0;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.375rem 1rem;
    color: var(--auger-text);
    cursor: pointer;
    white-space: nowrap;
  }
}

.navbar-divider {
  border: none;
  border-top: 1px solid var(--auger-border-light);
  margin: 0.25rem 0;
}

/* Component-specific customizations */
#nav {
  padding: 1rem;
  max-width: calc(var(--auger-section-max-width) * 0.9);
  margin: 0 auto;
  min-height: 84px;

  .navbar-brand {
    a {
      outline: none;
      background: none;
      img {
        max-height: inherit;
        min-width: 2.5rem;
      }
    }
  }
  .navbar-end {
    gap: 0.5rem;
    a {
      color: var(--auger-text-light);
      text-transform: uppercase;
      font-family: var(--auger-font-mono);
      font-weight: 500;
      font-size: var(--auger-size-7);
      outline: none;
      border-radius: 4px;
    }
    a.router-link-active,
    a.router-link-active[href]:hover {
      color: var(--auger-primary);
    }
    a[href]:hover {
      background: var(--auger-bg-dark);
      color: var(--auger-text-light);
    }
    a:focus {
      background: none;
    }
    a.navbar-search {
      font-size: 1.125rem;
      padding: 0.5rem 0.75rem;
      margin-left: 0.25rem;
    }
  }
  .navbar-menu {
    .navbar-link {
      user-select: none;
      border-radius: 4px 4px 0 0;
    }
    &:not(.is-active) .navbar-link:has(+ .navbar-dropdown:hover) {
      --auger-navbar-item-bg-l-delta: var(--auger-navbar-item-hover-bg-l-delta);
      --auger-navbar-item-bg-alpha: 1;
    }
    &.is-active .navbar-link:has(+ .navbar-dropdown):hover {
      background: none;
    }
    .navbar-dropdown {
      border-radius: 4px 0 4px 4px;
      border: none;
    }
  }

  .is-active .avatar {
    margin-left: 3em;
  }

  @media screen and (max-width: 782px) {
    padding: 0.75rem 1rem;
    min-height: auto;
    max-width: none;

    .navbar-brand {
      align-items: center;
    }

    .navbar-end {
      gap: 0;
      margin-left: 0;
    }
    .navbar-menu .navbar-dropdown {
      position: static;
      display: block;
      background: transparent;
      border-radius: 0;
      box-shadow: none;
    }
    .navbar-menu .navbar-link {
      display: none;
    }
  }
}
#feedback-button {
  background: var(--auger-bg-muted);
  color: var(--auger-text-weak);
  padding: 0.5em;
  position: fixed;
  right: 0;
  bottom: 75%;
  * {
    vertical-align: middle;
  }
  .text {
    display: inline-block;
    overflow: hidden;
    width: 0;
  }
  &:hover .text {
    width: auto;
  }
}
@media (max-width: 782px) {
  #feedback-button {
    background: none;
    bottom: auto;
    display: none;
    position: absolute;
    right: 80px;
    top: 24px;
    z-index: 30;
    .text {
      width: auto;
    }
  }
  #nav:has(.navbar-menu.is-active) + #feedback-button {
    display: block;
  }
}
</style>
